import { Action, MemoizedSelector, Store } from '@ngrx/store';
import { combineLatest, map, Observable, take } from 'rxjs';

export const enum CallStateType {
  INITIAL,
  LOADING,
  SUCCESS,
  ERROR,
}

export const initialStateResult: ResultStateInitial = {
  result: null,
  state: CallStateType.INITIAL,
};

export function stateLoading<T>(result: T | null): ResultStateLoading<T | null> {
  return {
    result,
    state: CallStateType.LOADING,
  };
}

export function stateSuccess<T>(result: T): ResultStateSuccess<T> {
  return {
    result,
    state: CallStateType.SUCCESS,
  };
}

export function stateError<ErrType, ResultType>(
  error: ErrType,
  result: ResultType | null,
): ResultStateError<ResultType | null, ErrType> {
  return {
    result,
    state: CallStateType.ERROR,
    error,
  };
}

export interface ResultStateInitial {
  readonly result: null;
  readonly state: CallStateType.INITIAL;
}

export interface ResultStateLoading<ResultType> {
  readonly result: ResultType | null;
  readonly state: CallStateType.LOADING;
}

export interface ResultStateSuccess<ResultType> {
  readonly result: ResultType;
  readonly state: CallStateType.SUCCESS;
}

export interface ResultStateError<ResultType, ErrorType> {
  readonly result: ResultType | null;
  readonly state: CallStateType.ERROR;
  readonly error: ErrorType;
}

export type ResultState<ResultType, ErrorType = string> =
  | ResultStateInitial
  | ResultStateLoading<ResultType | null>
  | ResultStateSuccess<ResultType>
  | ResultStateError<ResultType | null, ErrorType>;

export function selectResult<T extends ResultState<unknown, unknown>>(state: T): T['result'] {
  return state.result;
}

export function selectResultOrEmptyArray<T extends ResultState<Array<unknown>, unknown>>(
  state: T,
): NonNullable<T['result']> {
  return state.result ?? [];
}

export function isStateLoading(s: ResultState<unknown, unknown>): s is ResultStateLoading<unknown> {
  return s.state === CallStateType.LOADING;
}

/**
 * If equalFn returns true, this indicates that the action should not be dispatched because the expected item is already in the state
 */
export function maybeDispatch<T, U>(
  store: Store<T>, // eslint-disable-line @ngrx/no-typed-global-store
  selector: MemoizedSelector<T, U>,
  equalFn: (element: U) => boolean,
  action: Action,
) {
  store
    .select(selector)
    .pipe(take(1))
    .subscribe((result) => {
      if (!equalFn(result)) {
        store.dispatch(action);
      }
    });
}

/**
 * Dispatches `action` based on the value of the result returned by `selector`
 * Used for dispatching actions to fetch data that should only be fetched once when loading
 * a part of the dashboard.
 */
export function maybeDispatchAction<T, U extends ResultState<unknown>>(
  // Disable no-typed-global-store otherwise the type cannot be correctly inferred on the 'selector' variable
  store: Store<T>, // eslint-disable-line @ngrx/no-typed-global-store
  selector: MemoizedSelector<T, U>,
  action: Action,
) {
  // When loading or successful, this action shouldn't be dispatched again.
  maybeDispatch(
    store,
    selector,
    (state: U) => [CallStateType.SUCCESS, CallStateType.LOADING].includes(state.state),
    action,
  );
}

/**
 * Dispatches `action` based on the value of the result returned by `selector`
 * Used for dispatching actions to fetch data that should only be fetched once when loading
 * a part of the dashboard.
 * Optionally, if the requesting id is different (to use when fetching details of 1 item), it should be dispatched again.
 */
export function dispatchWhenNotLoaded<T, U extends ResultState<unknown, unknown>, ActionType extends Action>(
  store: Store<T>,
  selector: MemoizedSelector<T, U>,
  action: ActionType,
  idProperty?: keyof ActionType,
) {
  return maybeDispatch(
    store,
    selector,
    (state: U) => state.state === CallStateType.SUCCESS || state.state === CallStateType.LOADING,
    action,
  );
}

/**
 * Combines multiple loading selectors into one. When one of the selectors emits true, the result will be true.
 */
export function combineLoadingSelectors(
  store: Store,
  ...selectors: MemoizedSelector<object, boolean>[]
): Observable<boolean> {
  return combineLatest(selectors.map((selector) => store.select(selector))).pipe(
    map((states) => states.some((loading) => loading)),
  );
}
